import React, {CSSProperties, useEffect, useRef, useState} from "react";
import shadowClient from "../lib/clients/shadow/shadowClient";
import {ClickAwayListener, MenuList, Popper} from "@mui/material";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import {User} from "../lib/stores/UserStore";
import {useTheme} from "@mui/material/styles";
import Logger from "../lib/Logger";
import {renderToString} from "react-dom/server";
import Mention, {createMentionStyle} from "./Mention";
import discord_emojis from "../../data/discord_emojis.json";
import {transformHtml} from "./transformHtml";
import {setCursorToEnd} from "./utils";
import {Emoji} from "./Emoji";
import {notifier} from "../lib/Notifier";
import uiStore from "../lib/stores/uiStore";
import Avatar from "../Common/Avatar";
import Box from "../Common/Box";

const emojis = discord_emojis;
const emojiNames = Object.keys(emojis);
const logger = new Logger("MessageInput3");

export interface MessageInputProps {
    onSend: (content: string) => Promise<void>
    onEscape?: () => void
    placeholder?: string
    inputRef?: React.RefObject<HTMLDivElement>
    rootRef?: React.MutableRefObject<HTMLDivElement>
    style?: CSSProperties
    controlRef?: React.MutableRefObject<{ handleSend?: () => void }>
}

export default function BasicInput(
        {
            controlRef,
            rootRef: passedRootRef,
            inputRef: passedInputRef,
            onSend,
            onEscape,
            placeholder,
            style
        }: MessageInputProps) {

    const inputRef = passedInputRef || useRef<HTMLDivElement>(null);
    const rootRef = passedRootRef || inputRef;
    const [value, setValue] = useState("");
    const timeoutRef = useRef<number>();
    const [filteredOptions, setFilteredOptions] = useState<User[] | string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const theme = useTheme();

    const {background, color} = createMentionStyle(theme);

    useEffect(() => {
        if (inputRef.current) {
            const newStr = transformHtml(inputRef.current.innerHTML, background, color);
            setValue(newStr);
            inputRef.current.innerHTML = newStr;
        }
    }, [theme])

    function createUi(option: User | string): string {
        if (typeof option === "string") {
            // @ts-ignore
            const emojiStr = discord_emojis[option];
            return renderToString(<Emoji emojiStr={emojiStr}/>);
        }

        return createMention(option);
    }

    const addOption = (option: User | string) => {
        if (!inputRef.current) {
            throw new Error("inputRef not set");
        }

        const innerHtml = inputRef.current?.innerHTML
        const symbol = typeof option === "string" ? ":" : "@";
        const index = innerHtml.lastIndexOf(symbol);
        const startStr = innerHtml.slice(0, index);
        inputRef.current.innerHTML = startStr + createUi(option) + '&#8203;&#x200B;';

        setFilteredOptions([]);
        setValue(inputRef.current.innerHTML);
        inputRef.current.focus();
        setCursorToEnd(inputRef.current);
    }


    const createMention = (user: User): string => {
        return renderToString(<Mention initialStyle={createMentionStyle(theme)} user={user}/>);
    }

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
                inputRef.current &&
                inputRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setFilteredOptions([]);
    };

    const isOpen = filteredOptions.length > 0;

    async function handleListKeyDown(event: React.KeyboardEvent) {

        let allowDefault = false;

        if (isOpen) {
            if (event.key === 'ArrowDown') {
                if (selectedIndex < filteredOptions.length - 1) {
                    setSelectedIndex(selectedIndex + 1);
                }

            } else if (event.key === 'ArrowUp') {
                if (selectedIndex > 0) {
                    setSelectedIndex(selectedIndex - 1);
                }

            } else if (event.key === 'Tab') {
                const option = filteredOptions[selectedIndex];
                if (option) {
                    addOption(option)
                }
            } else if (event.key === 'Escape') {
                setFilteredOptions([]);
            } else {
                allowDefault = true;
            }

            if (!allowDefault) {
                event.preventDefault();
            }
        } else if (event.key === 'Escape') {
            if (onEscape) {
                onEscape()
            }

        }

        if (event.key === "Enter") {
            if (!uiStore.isMobile) {
                await handleSend(event);
            }
        }

    }

    function handleClick(option: User | string) {
        addOption(option)
    }

    const users = shadowClient.userStore.users;

    async function handleSend(event?: React.KeyboardEvent) {
        if (!inputRef.current) {
            throw new Error("inputRef not set");
        }

        try {
            // inputRef.current.focus();

            if (event?.shiftKey) {
                return;
            }

            const messageContent = transformHtml(inputRef.current.innerHTML, background, color, true);

            if (event) {
                event.preventDefault();
            }

            await onSend(messageContent);
            inputRef.current.innerHTML = "";
            setFilteredOptions([]);
            setValue("");

            // if (messageContent) {
            //     await onSend(messageContent);
            //     inputRef.current.innerHTML = "";
            //     setFilteredOptions([]);
            //     setValue("");
            //
            // } else {
            //     inputRef.current.innerHTML = inputRef.current.innerHTML + '<br>';
            //     setCursorToEnd(inputRef.current);
            // }

        } catch (e: any) {
            logger.error("Error while sending message. Error: %s " + e?.stack || e || e?.message);
            const errorMessage = "Error while sending message: " + e.message;
            notifier.error(errorMessage)
        }
    }

    if (controlRef) {
        controlRef.current = {
            handleSend,
        };
    }

    return <>
        {!value && placeholder && <div
                style={{
                    opacity: 0.5,
                    pointerEvents: "none",
                    position: "absolute",
                    left: "50px",
                    right: "0px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
        >{placeholder}</div>}

        <div
                ref={inputRef}
                style={{
                    flex: 1,
                    alignItems: "center",
                    outline: "none",
                    wordBreak: "break-word",
                    maxHeight: "100px",
                    overflow: "auto",
                    ...style,
                }}
                suppressContentEditableWarning={true}
                // contentEditable={clientService.isChrome() ? "plaintext-only" : true}
                contentEditable={true}
                onInput={(e) => {

                    const roomId = shadowClient.getJoinedRoomId();

                    if (!timeoutRef.current && roomId) {
                        shadowClient.triggerTyping(roomId).catch(e => logger.error("Error while triggering typing: %s", e))
                        timeoutRef.current = window.setTimeout(() => {
                            timeoutRef.current = undefined
                        }, 2000);
                    }

                    // @ts-ignore
                    setValue(e.target.innerHTML);
                    // @ts-ignore
                    const newValue = e.target.innerText;

                    const els = newValue.match(/@?\w+|@/g)?.pop();

                    if (els?.startsWith("@")) {
                        const val = els.toLowerCase().trim().slice(1);
                        const matchingOptions = users.filter((option) =>
                                option.username?.toLowerCase().trim().includes(val),
                        );

                        if (matchingOptions[0]) {
                            if (matchingOptions.length !== filteredOptions.length) {
                                setSelectedIndex(0);
                            }

                        } else {
                            setSelectedIndex(0);
                        }

                        setFilteredOptions(matchingOptions);
                    } else {
                        const emj = newValue.match(/:?\w+:?|:/g)?.pop();
                        if (emj?.startsWith(":") && emj.length > 2) {
                            const val = emj.toLowerCase().trim().slice(1);
                            const matchingOptions = emojiNames.filter((option) =>
                                    option.toLowerCase().trim().includes(val),
                            );

                            if (matchingOptions.length !== filteredOptions.length) {
                                setSelectedIndex(0);
                            }

                            setFilteredOptions(matchingOptions);

                        } else {
                            setFilteredOptions([]);
                        }
                    }

                }}
                onKeyDown={handleListKeyDown}
        />

        <Popper
                sx={{
                    maxHeight: "200px",
                    overflow: "auto",
                }}
                open={filteredOptions.length > 0}
                anchorEl={rootRef.current}
                role={undefined}
                placement="top-start"
                style={{width: rootRef.current?.clientWidth}}
        >
            <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                    <MenuList
                            id="composition-menu"
                            aria-labelledby="composition-button"
                            onKeyDown={handleListKeyDown}
                    >
                        {filteredOptions.map((option, i) =>
                                <MenuItem
                                        key={i}
                                        selected={selectedIndex === i}
                                        autoFocus={selectedIndex === i}
                                        onFocus={() => {
                                            inputRef.current?.focus();
                                        }}
                                        onClick={() => handleClick(option)}>
                                    {(option as User).username ? <Box
                                                    row
                                                    sx={{
                                                        marginBottom: "6px",
                                                        alignItems: "center",
                                                    }}
                                            >
                                                <Avatar user={(option as User)}/>
                                                <Box
                                                        sx={{
                                                            marginLeft: "10px",
                                                        }}
                                                        fontWeight={500}
                                                >
                                                    {/*@todo: this might cause problems if author doesnt exist*/}
                                                    {(option as User).username}
                                                </Box>
                                            </Box> :

                                            <Box
                                                    row
                                                    sx={{
                                                        alignItems: "center",
                                                    }}
                                            >

                                                <Emoji
                                                        size={36}
                                                        // @ts-ignore
                                                        emojiStr={discord_emojis[(option as string)]}
                                                />
                                                <Box
                                                        sx={{
                                                            marginLeft: "10px",
                                                        }}
                                                        fontWeight={500}
                                                >
                                                    {option as string}
                                                </Box>
                                            </Box>

                                    }
                                </MenuItem>
                        )
                        }
                    </MenuList>
                </ClickAwayListener>
            </Paper>
        </Popper>

    </>
}

